import React, { useState } from 'react'
import {
  FileText,
  ChevronDown,
  ChevronRight,
  Check,
  Clock,
  Zap,
  Database,
  Workflow,
  Calendar,
  Filter,
} from 'lucide-react'
import {
  ResponsiveContainer as RechartsResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie as RechartsPie,
  Cell as RechartsCell,
} from 'recharts'
import styled, { css } from 'styled-components'

// Optimization categories data
const optimizationCategories = [
  {
    id: 'content',
    title: 'Content Gaps',
    subtitle: 'Missing Help Articles or FAQs',
    description:
      'Topics where employees repeatedly ask for clarity or instructions.',
    color: '#65C18C',
    icon: FileText,
    stats: {
      requests: 861,
      recommendations: 25,
    },
    topGaps: [
      'Transit pass activation and shipment status',
      'Parking deduction limits in NYC',
      'Micromobility reimbursement eligibility',
      'Adding dependents to benefits',
      'Updating commuter program during relocation',
    ],
  },
  {
    id: 'data',
    title: 'Data Gaps',
    subtitle: 'AI needs access to more employee or payroll data',
    description:
      'AI needs access to more employee or payroll data to respond automatically.',
    color: '#F3B73C',
    icon: Database,
    stats: {
      requests: 129,
      automation: '70%',
    },
    details:
      'Integrating ADP subsidy fields or transit card shipment tracking could resolve 70% automatically.',
  },
  {
    id: 'action',
    title: 'Action Gaps',
    subtitle: 'Employee requests requiring manual HR intervention',
    description:
      'Employee requests requiring manual HR or vendor intervention.',
    color: '#BFA6CC',
    icon: Workflow,
    stats: {
      requests: 257,
      automation: '45%',
    },
    details:
      '"Card replacement," "address change," or "program transfer." 257 cases this month; 45% could be automated via workflow APIs.',
  },
]

// Recommendations data
const recommendations = [
  {
    type: 'Content Gap',
    recommendation: 'Add "Lost Clipper card process" FAQ',
    impact: 'Reduce escalations by 15%',
    effort: 'Low',
    status: 'pending',
  },
  {
    type: 'Data Gap',
    recommendation: 'Integrate Gusto API for pre-tax balances',
    impact: 'Auto-resolve 60% of payroll queries',
    effort: 'Medium',
    status: 'pending',
  },
  {
    type: 'Action Gap',
    recommendation: 'Create automation: "Address change sync"',
    impact: 'Save 3 HR hours/week',
    effort: 'High',
    status: 'pending',
  },
  {
    type: 'Content Gap',
    recommendation: 'Add micromobility eligibility guide',
    impact: 'Reduce inquiries by 22%',
    effort: 'Low',
    status: 'enabled',
  },
  {
    type: 'Data Gap',
    recommendation: 'Connect transit card shipment API',
    impact: 'Auto-resolve 40% of status checks',
    effort: 'Medium',
    status: 'in-progress',
  },
]

// Sentiment data
const sentimentData = [
  { name: 'Positive', value: 72, color: '#65C18C' },
  { name: 'Neutral', value: 21, color: '#C9CCD1' },
  { name: 'Negative', value: 7, color: '#EF4444' },
]

const topicSentiment = [
  { name: 'Parking reimbursements', positive: 63, trend: '+8%', trendUp: true },
  { name: 'Transit pass delays', positive: 41, trend: '-5%', trendUp: false },
  { name: 'Micromobility coverage', positive: 78, trend: '+3%', trendUp: true },
  { name: 'Lost cards', positive: 49, trend: '-9%', trendUp: false },
  { name: 'Pre-tax eligibility', positive: 67, trend: '+2%', trendUp: true },
]

const programRecommendations = [
  {
    text: 'Add digital transit passes for remote employees to reduce lost-card volume',
    impact: '-9% lost card inquiries',
    implemented: false,
  },
  {
    text: 'Clarify parking policy caps in NYC region',
    impact: '17% of negative sentiment',
    implemented: false,
  },
  {
    text: 'Launch pilot micromobility stipend expansion',
    impact: '+12% satisfaction correlation',
    implemented: false,
  },
  {
    text: 'Automate new-hire commuter benefit onboarding',
    impact: '-45% first-week HR inquiries',
    implemented: false,
  },
  {
    text: 'Update FAQs on pre-tax deduction timing before payroll cutoff',
    impact: 'Reduce monthly escalations',
    implemented: false,
  },
]

const MainContent = styled.main`
  flex: 1;
  overflow: auto;
`

const ContentContainer = styled.div`
  padding: 2rem;
`

const HeaderSection = styled.div`
  margin-bottom: 2rem;
`

const PageTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`

const PageDescription = styled.p`
  font-size: 1rem;
  color: #4b5563;
`

const FilterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`

const FilterButtonIcon = styled.button`
  padding: 0.5rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }
`

const ButtonText = styled.span`
  color: #374151;
`

const Section = styled.div`
  margin-bottom: 3rem;
`

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
`

const OptimizationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const CategoryCard = styled.div<{ $borderColor: string }>`
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  border-left-width: 4px;
  border-left-color: ${(props) => props.$borderColor};
  overflow: hidden;
  transition: all 0.3s;

  &:hover {
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`

const CategoryButton = styled.button`
  width: 100%;
  padding: 1.5rem;
  text-align: left;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  border: none;
  background: none;
  cursor: pointer;
`

const IconContainer = styled.div<{ $backgroundColor: string }>`
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: ${(props) => props.$backgroundColor};
`

const CategoryContent = styled.div`
  flex: 1;
`

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`

const CategoryTitle = styled.h3`
  font-weight: 600;
  color: #111827;
`

const CategorySubtitle = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
`

const CategoryStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
`

const CategoryStatValue = styled.div<{ $color: string }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => props.$color};
`

const CategoryStatLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`

const CategoryExpanded = styled.div`
  padding: 0.5rem 1.5rem 1.5rem 1.5rem;
  border-top: 1px solid #f3f4f6;
`

const CategoryDescription = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 1rem;
`

const GapsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const GapsTitle = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
`

const GapsListItems = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const GapItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
`

const GapBullet = styled.span`
  color: #9ca3af;
  margin-top: 0.125rem;
`

const CategoryDetails = styled.p`
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.625;
`

const FlowCard = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
`

const FlowTitle = styled.h3`
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
`

const FlowContainer = styled.div`
  position: relative;
  height: 24rem;
`

const FlowSVG = styled.svg`
  overflow: visible;
`

const FlowLabel = styled.div`
  position: absolute;
  font-size: 0.75rem;
`

const FlowLabelLeft = styled(FlowLabel)`
  top: 1rem;
  left: 0;
`

const FlowLabelTitle = styled.div`
  font-weight: 600;
  color: #111827;
`

const FlowLabelValue = styled.div<{ $color: string; $size?: string }>`
  font-weight: 700;
  color: ${(props) => props.$color};
  font-size: ${(props) => props.$size || '1.5rem'};
`

const FlowLabelRight = styled.div`
  position: absolute;
  top: 1rem;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  font-size: 0.75rem;
`

const FlowLabelItem = styled.div``

const RecommendationsCard = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`

const RecommendationsHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`

const RecommendationsTitle = styled.h3`
  font-weight: 600;
  color: #111827;
`

const TableContainer = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
`

const TableHead = styled.thead`
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`

const TableRow = styled.tr<{ $highlighted?: boolean }>`
  transition: background-color 0.2s;

  ${(props) =>
    props.$highlighted &&
    css`
      background-color: #f0fdf4;
    `}
`

const TableHeader = styled.th`
  padding: 0.75rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
`

const TableBody = styled.tbody``

const TableCell = styled.td`
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  color: #374151;
`

const TableCellPrimary = styled(TableCell)`
  color: #111827;
`

const EffortBadge = styled.span<{ $effort: 'Low' | 'Medium' | 'High' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;

  ${(props) => {
    if (props.$effort === 'Low') {
      return css`
        background-color: #dcfce7;
        color: #166534;
      `
    }
    if (props.$effort === 'Medium') {
      return css`
        background-color: #fef3c7;
        color: #92400e;
      `
    }
    return css`
      background-color: #fee2e2;
      color: #991b1b;
    `
  }}
`

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const StatusText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
`

const StatusEnabled = styled(StatusContainer)`
  color: #15803d;
`

const StatusInProgress = styled(StatusContainer)`
  color: #1d4ed8;
`

const AcceptButton = styled.button`
  padding: 0.375rem 0.75rem;
  background-color: #5c1631;
  color: white;
  font-size: 0.875rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #4a1127;
  }
`

const ProgramSection = styled.div`
  border-top: 4px solid #5c1631;
  padding-top: 3rem;
`

const ProgramHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

const ProgramHeaderBar = styled.div`
  width: 0.25rem;
  height: 2rem;
  background-color: #5c1631;
  border-radius: 9999px;
`

const ProgramTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`

const ProgramDescription = styled.p`
  color: #4b5563;
  margin-bottom: 2rem;
`

const SentimentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const SentimentCard = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
`

const SentimentCardTitle = styled.h3`
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
`

const SentimentChartContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`

const PieChartWrapper = styled.div`
  position: relative;
  width: 12rem;
  height: 12rem;
`

const PieChartCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const PieChartCenterContent = styled.div`
  text-align: center;
`

const PieChartValue = styled.div`
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
`

const PieChartLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`

const SentimentLegend = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const SentimentLegendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const SentimentLegendInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SentimentDot = styled.div<{ $color: string }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background-color: ${(props) => props.$color};
`

const SentimentLegendName = styled.span`
  font-size: 0.875rem;
  color: #374151;
`

const SentimentLegendValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
`

const TopicSentimentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const TopicSentimentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const TopicSentimentContent = styled.div`
  flex: 1;
`

const TopicSentimentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`

const TopicSentimentName = styled.span`
  font-size: 0.875rem;
  color: #374151;
`

const TopicSentimentStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TopicSentimentValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
`

const TopicSentimentTrend = styled.span<{ $positive: boolean }>`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${(props) => (props.$positive ? '#16a34a' : '#dc2626')};
`

const TopicSentimentBar = styled.div`
  height: 0.5rem;
  background-color: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
`

const TopicSentimentFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  border-radius: 9999px;
  transition: all 0.5s;
  width: ${(props) => props.$width}%;
  background-color: ${(props) => props.$color};
`

const RecommendationsSection = styled.div`
  background: linear-gradient(to bottom right, #faf5f6, #f5f0f5);
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  padding: 2rem;
`

const RecommendationsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

const RecommendationsIconContainer = styled.div`
  padding: 0.5rem;
  background-color: #5c1631;
  border-radius: 0.5rem;
`

const RecommendationsSectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
`

const RecommendationsSectionDescription = styled.p`
  color: #4b5563;
  margin-bottom: 1.5rem;
`

const RecommendationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const RecommendationItem = styled.div<{ $implemented: boolean }>`
  background-color: white;
  border-radius: 0.5rem;
  border: 1px solid;
  padding: 1rem;
  transition: all 0.3s;

  ${(props) =>
    props.$implemented
      ? css`
          border-color: #10b981;
          background-color: #f0fdf4;
        `
      : css`
          border-color: #e5e7eb;
        `}
`

const RecommendationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`

const Checkbox = styled.button<{ $checked: boolean }>`
  margin-top: 0.25rem;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.25rem;
  border: 2px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  cursor: pointer;
  background: none;

  ${(props) =>
    props.$checked
      ? css`
          background-color: #10b981;
          border-color: #10b981;
        `
      : css`
          border-color: #d1d5db;

          &:hover {
            border-color: #5c1631;
          }
        `}
`

const RecommendationText = styled.div`
  flex: 1;
`

const RecommendationTextContent = styled.p<{ $implemented: boolean }>`
  font-size: 0.875rem;
  line-height: 1.625;
  color: ${(props) => (props.$implemented ? '#4b5563' : '#111827')};
  ${(props) => props.$implemented && 'text-decoration: line-through;'}
`

const RecommendationTags = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const ImpactTag = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #5c1631;
  background-color: rgba(92, 22, 49, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
`

const ImplementedTag = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #15803d;
  background-color: #dcfce7;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
`

const Optimize = React.memo(() => {
  const [expandedCategory, setExpandedCategory] = useState('content')
  const [recommendationStatuses, setRecommendationStatuses] = useState(
    recommendations.map((r) => r.status)
  )
  const [implementedRecommendations, setImplementedRecommendations] = useState(
    programRecommendations.map(() => false)
  )

  const toggleRecommendation = (index: number) => {
    const newStatuses = [...recommendationStatuses]
    if (newStatuses[index] === 'pending') {
      newStatuses[index] = 'enabled'
    }
    setRecommendationStatuses(newStatuses)
  }

  const toggleProgramRecommendation = (index: number) => {
    const newImplemented = [...implementedRecommendations]
    newImplemented[index] = !newImplemented[index]
    setImplementedRecommendations(newImplemented)
  }

  const getSentimentColor = (positive: number) => {
    if (positive >= 70) return '#65C18C'
    if (positive >= 50) return '#F3B73C'
    return '#EF4444'
  }

  return (
    <div>
      {/* Main Content */}
      <MainContent>
        <ContentContainer>
          {/* Header */}
          <HeaderSection>
            <PageTitle>Optimize</PageTitle>
            <PageDescription>
              Identify gaps and opportunities to improve Fleet AI Concierge
              performance and employee experience
            </PageDescription>
          </HeaderSection>

          <FilterButtons>
            <FilterButton>
              <Calendar
                style={{ width: '1rem', height: '1rem', color: '#4b5563' }}
              />
              <ButtonText>Apr 1, 2025 - May 15, 2025</ButtonText>
            </FilterButton>
            <FilterButtonIcon>
              <Filter
                style={{ width: '1rem', height: '1rem', color: '#4b5563' }}
              />
            </FilterButtonIcon>
          </FilterButtons>

          {/* SECTION 1: AI Support Optimization */}
          <Section>
            <SectionTitle>AI Support Optimization</SectionTitle>

            <OptimizationGrid>
              {/* Left Panel: Optimization Categories */}
              <CategoriesContainer>
                {optimizationCategories.map((category) => {
                  const Icon = category.icon
                  const isExpanded = expandedCategory === category.id

                  return (
                    <CategoryCard
                      key={category.id}
                      $borderColor={category.color}
                    >
                      <CategoryButton
                        onClick={() =>
                          setExpandedCategory(isExpanded ? '' : category.id)
                        }
                      >
                        <IconContainer $backgroundColor={`${category.color}20`}>
                          <Icon
                            style={{
                              width: '1.25rem',
                              height: '1.25rem',
                              color: category.color,
                            }}
                          />
                        </IconContainer>
                        <CategoryContent>
                          <CategoryHeader>
                            <CategoryTitle>{category.title}</CategoryTitle>
                            {isExpanded ? (
                              <ChevronDown
                                style={{
                                  width: '1.25rem',
                                  height: '1.25rem',
                                  color: '#9ca3af',
                                }}
                              />
                            ) : (
                              <ChevronRight
                                style={{
                                  width: '1.25rem',
                                  height: '1.25rem',
                                  color: '#9ca3af',
                                }}
                              />
                            )}
                          </CategoryHeader>
                          <CategorySubtitle>
                            {category.subtitle}
                          </CategorySubtitle>
                          <CategoryStats>
                            <CategoryStatValue $color={category.color}>
                              {category.stats.requests}
                            </CategoryStatValue>
                            <CategoryStatLabel>
                              {category.id === 'content' &&
                                `${category.stats.recommendations} recommended articles`}
                              {category.id !== 'content' &&
                                `${category.stats.automation} could be automated`}
                            </CategoryStatLabel>
                          </CategoryStats>
                        </CategoryContent>
                      </CategoryButton>

                      {isExpanded && (
                        <CategoryExpanded>
                          <CategoryDescription>
                            {category.description}
                          </CategoryDescription>
                          {category.topGaps && (
                            <GapsList>
                              <GapsTitle>Top Gaps:</GapsTitle>
                              <GapsListItems>
                                {category.topGaps.map((gap, idx) => (
                                  <GapItem key={idx}>
                                    <GapBullet>•</GapBullet>
                                    <span>{gap}</span>
                                  </GapItem>
                                ))}
                              </GapsListItems>
                            </GapsList>
                          )}
                          {category.details && (
                            <CategoryDetails>
                              {category.details}
                            </CategoryDetails>
                          )}
                        </CategoryExpanded>
                      )}
                    </CategoryCard>
                  )
                })}
              </CategoriesContainer>

              {/* Right Panel: Flow Visualization */}
              <FlowCard>
                <FlowTitle>Request Flow</FlowTitle>

                <FlowContainer>
                  <FlowSVG width='100%' height='100%' viewBox='0 0 400 350'>
                    {/* Flow paths */}
                    <defs>
                      <linearGradient
                        id='greenGradient'
                        x1='0%'
                        y1='0%'
                        x2='100%'
                        y2='0%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#65C18C'
                          stopOpacity='0.3'
                        />
                        <stop
                          offset='100%'
                          stopColor='#65C18C'
                          stopOpacity='0.1'
                        />
                      </linearGradient>
                      <linearGradient
                        id='maroonGradient'
                        x1='0%'
                        y1='0%'
                        x2='100%'
                        y2='0%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#5C1631'
                          stopOpacity='0.3'
                        />
                        <stop
                          offset='100%'
                          stopColor='#5C1631'
                          stopOpacity='0.1'
                        />
                      </linearGradient>
                      <linearGradient
                        id='lavenderGradient'
                        x1='0%'
                        y1='0%'
                        x2='100%'
                        y2='0%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#BFA6CC'
                          stopOpacity='0.3'
                        />
                        <stop
                          offset='100%'
                          stopColor='#BFA6CC'
                          stopOpacity='0.1'
                        />
                      </linearGradient>
                    </defs>

                    {/* Inbound to Auto-Resolved (large flow) */}
                    <path
                      d='M 80 100 Q 180 100 280 80'
                      fill='none'
                      stroke='url(#greenGradient)'
                      strokeWidth='60'
                      opacity='0.6'
                    />

                    {/* Inbound to HR Routed */}
                    <path
                      d='M 80 120 Q 180 140 280 160'
                      fill='none'
                      stroke='url(#maroonGradient)'
                      strokeWidth='20'
                      opacity='0.6'
                    />

                    {/* Inbound to Vendor Escalated */}
                    <path
                      d='M 80 130 Q 180 180 280 220'
                      fill='none'
                      stroke='url(#lavenderGradient)'
                      strokeWidth='8'
                      opacity='0.6'
                    />

                    {/* Vertical bars */}
                    <rect
                      x='60'
                      y='50'
                      width='20'
                      height='120'
                      fill='#5C1631'
                      rx='4'
                    />
                    <rect
                      x='280'
                      y='50'
                      width='20'
                      height='60'
                      fill='#65C18C'
                      rx='4'
                    />
                    <rect
                      x='280'
                      y='140'
                      width='20'
                      height='40'
                      fill='#5C1631'
                      rx='4'
                    />
                    <rect
                      x='280'
                      y='200'
                      width='20'
                      height='30'
                      fill='#BFA6CC'
                      rx='4'
                    />
                    <rect
                      x='280'
                      y='250'
                      width='20'
                      height='20'
                      fill='#C9CCD1'
                      rx='4'
                    />
                  </FlowSVG>

                  {/* Labels */}
                  <FlowLabelLeft>
                    <FlowLabelTitle>Inbound Requests</FlowLabelTitle>
                    <FlowLabelValue $color='#5C1631' $size='1.5rem'>
                      4,060
                    </FlowLabelValue>
                  </FlowLabelLeft>

                  <FlowLabelRight>
                    <FlowLabelItem>
                      <FlowLabelTitle>Auto-Resolved by Fleet</FlowLabelTitle>
                      <FlowLabelValue $color='#65C18C' $size='1.25rem'>
                        3,184 (78%)
                      </FlowLabelValue>
                    </FlowLabelItem>
                    <FlowLabelItem>
                      <FlowLabelTitle>Routed to HR Team</FlowLabelTitle>
                      <FlowLabelValue $color='#5C1631' $size='1.25rem'>
                        876 (21%)
                      </FlowLabelValue>
                    </FlowLabelItem>
                    <FlowLabelItem>
                      <FlowLabelTitle>Escalated to Vendor</FlowLabelTitle>
                      <FlowLabelValue $color='#BFA6CC' $size='1.125rem'>
                        122 (3%)
                      </FlowLabelValue>
                    </FlowLabelItem>
                    <FlowLabelItem>
                      <FlowLabelTitle>Pending Follow-Up</FlowLabelTitle>
                      <FlowLabelValue $color='#6b7280' $size='1rem'>
                        52 (1%)
                      </FlowLabelValue>
                    </FlowLabelItem>
                  </FlowLabelRight>
                </FlowContainer>
              </FlowCard>
            </OptimizationGrid>

            {/* Recommendations Table */}
            <RecommendationsCard>
              <RecommendationsHeader>
                <RecommendationsTitle>
                  Recommended Optimizations
                </RecommendationsTitle>
              </RecommendationsHeader>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Type</TableHeader>
                      <TableHeader>Recommendation</TableHeader>
                      <TableHeader>Impact</TableHeader>
                      <TableHeader>Effort</TableHeader>
                      <TableHeader>Action</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recommendations.map((rec, idx) => {
                      const status = recommendationStatuses[idx]
                      const isEnabled = status === 'enabled'
                      const isInProgress = status === 'in-progress'

                      return (
                        <TableRow key={idx} $highlighted={isEnabled}>
                          <TableCellPrimary>{rec.type}</TableCellPrimary>
                          <TableCellPrimary>
                            {rec.recommendation}
                          </TableCellPrimary>
                          <TableCell>{rec.impact}</TableCell>
                          <TableCell>
                            <EffortBadge
                              $effort={rec.effort as 'Low' | 'Medium' | 'High'}
                            >
                              {rec.effort}
                            </EffortBadge>
                          </TableCell>
                          <TableCell>
                            {isEnabled ? (
                              <StatusEnabled>
                                <Check
                                  style={{ width: '1rem', height: '1rem' }}
                                />
                                <StatusText>Enabled</StatusText>
                              </StatusEnabled>
                            ) : isInProgress ? (
                              <StatusInProgress>
                                <Clock
                                  style={{ width: '1rem', height: '1rem' }}
                                />
                                <StatusText>In Progress</StatusText>
                              </StatusInProgress>
                            ) : (
                              <AcceptButton
                                onClick={() => toggleRecommendation(idx)}
                              >
                                Accept
                              </AcceptButton>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </RecommendationsCard>
          </Section>

          {/* SECTION 2: Program Optimization */}
          <ProgramSection>
            <ProgramHeader>
              <ProgramHeaderBar />
              <ProgramTitle>Program Optimization</ProgramTitle>
            </ProgramHeader>
            <ProgramDescription>
              Employee insights and recommendations to improve your commuter
              benefits program
            </ProgramDescription>

            {/* Sentiment Breakdown */}
            <SentimentGrid>
              {/* Donut Chart */}
              <SentimentCard>
                <SentimentCardTitle>
                  Employee Sentiment Overview
                </SentimentCardTitle>
                <SentimentChartContainer>
                  <PieChartWrapper>
                    <RechartsResponsiveContainer width='100%' height='100%'>
                      <RechartsPieChart>
                        <RechartsPie
                          data={sentimentData}
                          cx='50%'
                          cy='50%'
                          innerRadius={60}
                          outerRadius={80}
                          dataKey='value'
                          stroke='#fff'
                          strokeWidth={2}
                        >
                          {sentimentData.map((entry, index) => (
                            <RechartsCell
                              key={`cell-${index}`}
                              fill={entry.color}
                            />
                          ))}
                        </RechartsPie>
                      </RechartsPieChart>
                    </RechartsResponsiveContainer>
                    <PieChartCenter>
                      <PieChartCenterContent>
                        <PieChartValue>72%</PieChartValue>
                        <PieChartLabel>Positive</PieChartLabel>
                      </PieChartCenterContent>
                    </PieChartCenter>
                  </PieChartWrapper>
                  <SentimentLegend>
                    {sentimentData.map((item) => (
                      <SentimentLegendItem key={item.name}>
                        <SentimentLegendInfo>
                          <SentimentDot $color={item.color} />
                          <SentimentLegendName>{item.name}</SentimentLegendName>
                        </SentimentLegendInfo>
                        <SentimentLegendValue>
                          {item.value}%
                        </SentimentLegendValue>
                      </SentimentLegendItem>
                    ))}
                  </SentimentLegend>
                </SentimentChartContainer>
              </SentimentCard>

              {/* Topic Sentiment Heatmap */}
              <SentimentCard>
                <SentimentCardTitle>Sentiment by Topic</SentimentCardTitle>
                <TopicSentimentList>
                  {topicSentiment.map((topic) => (
                    <TopicSentimentItem key={topic.name}>
                      <TopicSentimentContent>
                        <TopicSentimentHeader>
                          <TopicSentimentName>{topic.name}</TopicSentimentName>
                          <TopicSentimentStats>
                            <TopicSentimentValue>
                              {topic.positive}%
                            </TopicSentimentValue>
                            <TopicSentimentTrend $positive={topic.trendUp}>
                              {topic.trend}
                            </TopicSentimentTrend>
                          </TopicSentimentStats>
                        </TopicSentimentHeader>
                        <TopicSentimentBar>
                          <TopicSentimentFill
                            $width={topic.positive}
                            $color={getSentimentColor(topic.positive)}
                          />
                        </TopicSentimentBar>
                      </TopicSentimentContent>
                    </TopicSentimentItem>
                  ))}
                </TopicSentimentList>
              </SentimentCard>
            </SentimentGrid>

            {/* AI-Driven Program Recommendations */}
            <RecommendationsSection>
              <RecommendationsSectionHeader>
                <RecommendationsIconContainer>
                  <Zap
                    style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      color: 'white',
                    }}
                  />
                </RecommendationsIconContainer>
                <RecommendationsSectionTitle>
                  AI-Driven Program Recommendations
                </RecommendationsSectionTitle>
              </RecommendationsSectionHeader>
              <RecommendationsSectionDescription>
                Fleet AI suggests exploring the following program optimizations:
              </RecommendationsSectionDescription>

              <RecommendationsList>
                {programRecommendations.map((rec, idx) => (
                  <RecommendationItem
                    key={idx}
                    $implemented={implementedRecommendations[idx]}
                  >
                    <RecommendationContent>
                      <Checkbox
                        $checked={implementedRecommendations[idx]}
                        onClick={() => toggleProgramRecommendation(idx)}
                      >
                        {implementedRecommendations[idx] && (
                          <Check
                            style={{
                              width: '0.75rem',
                              height: '0.75rem',
                              color: 'white',
                            }}
                          />
                        )}
                      </Checkbox>
                      <RecommendationText>
                        <RecommendationTextContent
                          $implemented={implementedRecommendations[idx]}
                        >
                          {rec.text}
                        </RecommendationTextContent>
                        <RecommendationTags>
                          <ImpactTag>{rec.impact}</ImpactTag>
                          {implementedRecommendations[idx] && (
                            <ImplementedTag>
                              Marked as implemented
                            </ImplementedTag>
                          )}
                        </RecommendationTags>
                      </RecommendationText>
                    </RecommendationContent>
                  </RecommendationItem>
                ))}
              </RecommendationsList>
            </RecommendationsSection>
          </ProgramSection>
        </ContentContainer>
      </MainContent>
    </div>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  Optimize.displayName = 'Optimize'
}

export default Optimize
